// src/components/MascotWidget.js
import React from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import { keyframes } from '@emotion/react';

const MIN_KEY = 'cp:ui:mascot:min';
const HIDE_KEY = 'hide_mascot';
const POS_KEY = 'cp:ui:mascot:pos'; // stores { lp: number (0..1), bp: number (0..1) }

const prefersReducedMotion = () => {
  try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch { return false; }
};

// Gentle bobbing via CSS (GPU-friendly)
const bob = keyframes`
  0%, 100% { transform: translateY(-2px); }
  50%      { transform: translateY(0); }
`;

function MascotWidget() {
  const [visible, setVisible] = React.useState(() => {
    try { return localStorage.getItem(HIDE_KEY) !== '1'; } catch { return true; }
  });
  const [minimized, setMinimized] = React.useState(() => {
    try { return localStorage.getItem(MIN_KEY) === '1'; } catch { return false; }
  });
  const [ready, setReady] = React.useState(false);

  // Track viewport for pixel conversion
  const [vp, setVp] = React.useState({ w: 0, h: 0 });
  // Position stored as percentages of viewport
  const [pos, setPos] = React.useState({ lp: 0, bp: 0 }); // left%, bottom%

  // Defer mount until idle to avoid competing with LCP
  React.useEffect(() => {
    let didSet = false;
    const idle = () => {
      try {
        const ric = window.requestIdleCallback || ((fn) => setTimeout(fn, 450));
        ric(() => { if (!didSet) { setReady(true); didSet = true; } });
      } catch {
        setTimeout(() => { if (!didSet) { setReady(true); didSet = true; } }, 450);
      }
    };
    idle();
    return () => { didSet = true; };
  }, []);

  // Initialize viewport and position
  React.useEffect(() => {
    if (!ready) return;
    const updateVp = () => setVp({ w: Math.max(1, window.innerWidth), h: Math.max(1, window.innerHeight) });
    updateVp();
    window.addEventListener('resize', updateVp);
    // Init position from storage or default margins (16px/14px-ish)
    try {
      const saved = localStorage.getItem(POS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (typeof parsed?.lp === 'number' && typeof parsed?.bp === 'number') {
          setPos({ lp: Math.min(1, Math.max(0, parsed.lp)), bp: Math.min(1, Math.max(0, parsed.bp)) });
        } else {
          setPos({ lp: 16 / window.innerWidth, bp: 16 / window.innerHeight });
        }
      } else {
        setPos({ lp: 16 / window.innerWidth, bp: 16 / window.innerHeight });
      }
    } catch {
      setPos({ lp: 16 / window.innerWidth, bp: 16 / window.innerHeight });
    }
    return () => window.removeEventListener('resize', updateVp);
  }, [ready]);

  // Wire global events so Settings and other UI can control visibility and position
  React.useEffect(() => {
    const onSet = (e) => {
      const v = e?.detail?.visible;
      if (typeof v === 'boolean') {
        setVisible(v);
        try { localStorage.setItem(HIDE_KEY, v ? '0' : '1'); } catch {}
      }
    };
    const onShow = () => { setVisible(true); try { localStorage.setItem(HIDE_KEY, '0'); } catch {} };
    const onHide = () => { setVisible(false); try { localStorage.setItem(HIDE_KEY, '1'); } catch {} };
    const onResetPos = () => {
      try {
        const lp = 16 / Math.max(1, window.innerWidth);
        const bp = 16 / Math.max(1, window.innerHeight);
        setPos({ lp, bp });
        localStorage.setItem(POS_KEY, JSON.stringify({ lp, bp }));
      } catch {
        setPos({ lp: 0.02, bp: 0.03 });
      }
    };
    window.addEventListener('cp:mascot:set', onSet);
    window.addEventListener('cp:mascot:show', onShow);
    window.addEventListener('cp:mascot:hide', onHide);
    window.addEventListener('cp:mascot:resetpos', onResetPos);
    return () => {
      window.removeEventListener('cp:mascot:set', onSet);
      window.removeEventListener('cp:mascot:show', onShow);
      window.removeEventListener('cp:mascot:hide', onHide);
      window.removeEventListener('cp:mascot:resetpos', onResetPos);
    };
  }, []);


  const toggleMinimize = React.useCallback(() => {
    setMinimized((m) => {
      const next = !m;
      try { localStorage.setItem(MIN_KEY, next ? '1' : '0'); } catch {}
      return next;
    });
  }, []);

  const onImgError = React.useCallback(() => {
    // If the image fails to load, shrink to the mini button to stay unobtrusive
    setMinimized(true);
  }, []);

  // Drag to reposition (expanded only)
  const dragRef = React.useRef({
    active: false, sx: 0, sy: 0, leftPx: 0, bottomPx: 0, raf: 0, lastLp: 0, lastBp: 0,
  });
  const startDrag = (e) => {
    if (minimized) return;
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch {}
    const rect = { w: vp.w, h: vp.h };
    const leftPx = Math.round(pos.lp * rect.w);
    const bottomPx = Math.round(pos.bp * rect.h);
    dragRef.current = { active: true, sx: e.clientX, sy: e.clientY, leftPx, bottomPx, raf: 0 };
    e.preventDefault?.();
  };
  const onMove = (e) => {
    const st = dragRef.current;
    if (!st.active) return;
    const dx = e.clientX - st.sx;
    const dy = e.clientY - st.sy;
    const margin = 8;
    const maxLeft = Math.max(0, vp.w - margin);
    const maxBottom = Math.max(0, vp.h - margin);
    let newLeft = Math.min(maxLeft, Math.max(margin, st.leftPx + dx));
    let newBottom = Math.min(maxBottom, Math.max(margin, st.bottomPx - dy)); // invert Y (up increases bottom)
    // rAF throttle
    if (!st.raf) {
      st.raf = requestAnimationFrame(() => {
        st.raf = 0;
        const lp = newLeft / Math.max(1, vp.w);
        const bp = newBottom / Math.max(1, vp.h);
        // record latest position for reliable persistence on drag end
        st.lastLp = lp;
        st.lastBp = bp;
        setPos({ lp, bp });
      });
    }
    e.preventDefault?.();
  };
  const endDrag = (e) => {
    const st = dragRef.current;
    if (!st.active) return;
    dragRef.current.active = false;
    if (st.raf) cancelAnimationFrame(st.raf);
    const lp = typeof st.lastLp === 'number' ? st.lastLp : pos.lp;
    const bp = typeof st.lastBp === 'number' ? st.lastBp : pos.bp;
    try { localStorage.setItem(POS_KEY, JSON.stringify({ lp, bp })); } catch {}
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch {}
  };

  if (!visible || !ready) return null;

  const reduce = prefersReducedMotion();
  const size = { mini: 44, miniXS: 48 };
  const shadow = [
    'drop-shadow(0 0 3px rgba(255,255,255,0.55))',      // subtle white edge glow
    'drop-shadow(0 0 10px rgba(125,200,255,0.28))',     // faint cool halo
    'drop-shadow(0 10px 20px rgba(0,0,0,0.35))',        // existing soft drop shadow
  ].join(' ');
  const leftPx = Math.round(pos.lp * Math.max(1, vp.w));
  const bottomPx = Math.round(pos.bp * Math.max(1, vp.h));

  return (
    <>
      {!minimized ? (
        <Box
          sx={{
            position: 'fixed',
            left: leftPx,
            bottom: bottomPx,
            zIndex: 1400,
            pointerEvents: 'none', // only buttons inside should be interactive
            touchAction: 'none',
          }}
        >
          <Box sx={{ position: 'relative', pointerEvents: 'auto' }}>
            <Tooltip title="Drag to move. Tap - to minimize." arrow>
              <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'flex-end' }}>
                <Box
                  component="img"
                  src="/cpmascot.png"
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  decoding="async"
                  onError={onImgError}
                  onDragStart={(e) => e.preventDefault?.()}
                  onPointerDown={startDrag}
                  onPointerMove={onMove}
                  onPointerUp={endDrag}
                  onPointerCancel={endDrag}
                  sx={{
                    height: 'auto',
                    width: 'auto',
                    // ~30% smaller on mobile (xs), unchanged on sm+ (mobile +10%)
                    maxHeight: { xs: 'min(11vh, 92px)', sm: 'min(18vh, 140px)' },
                    filter: shadow,
                    userSelect: 'none',
                    willChange: reduce ? undefined : 'transform',
                    animation: reduce ? undefined : `${bob} 4.6s ease-in-out infinite`,
                    cursor: 'grab',
                    display: 'block',
                  }}
                />
                {/* Controls */}
                <IconButton
                  size="small"
                  onClick={toggleMinimize}
                  aria-label="Minimize mascot"
                  sx={{
                    position: 'absolute',
                    top: -8,       // sit just above the image
                    right: -8,     // and slightly outside to the right
                    width: 24,
                    height: 24,
                    minWidth: 24,
                    minHeight: 24,
                    p: 0,
                    bgcolor: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.22)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                    '&:focus-visible': {
                      outline: 'none',
                      boxShadow: '0 0 0 2px rgba(125,200,255,0.55), 0 0 0 4px rgba(255,255,255,0.28), 0 4px 12px rgba(0,0,0,0.35)',
                    },
                    boxShadow: '0 4px 12px rgba(0,0,0,0.35)',
                  }}
                >
                  <RemoveRoundedIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            </Tooltip>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            position: 'fixed',
            left: leftPx,
            bottom: bottomPx,
            zIndex: 1400,
            pointerEvents: 'auto',
            touchAction: 'manipulation',
          }}
        >
          <Tooltip title="Show mascot" arrow>
            <IconButton
              onClick={toggleMinimize}
              aria-label="Show mascot"
              sx={{
                width: { xs: size.miniXS, sm: size.mini },
                height: { xs: size.miniXS, sm: size.mini },
                borderRadius: '50%',
                bgcolor: 'rgba(0,0,0,0.55)',
                border: '1px solid rgba(255,255,255,0.18)',
                overflow: 'hidden',
                boxShadow: '0 0 0 2px rgba(255,255,255,0.35), 0 0 8px rgba(125,200,255,0.30), 0 6px 16px rgba(0,0,0,0.35)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              }}
            >
              <img
                src="/cpmascot.png"
                alt=""
                aria-hidden="true"
                loading="lazy"
                decoding="async"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </>
  );
}

export default React.memo(MascotWidget);
